/**
 * EnemySprites - Pixel art sprite definitions for enemies
 */

// Color palette for enemy sprites
const C = {
  // Transparent
  _: null,
  // Zombie colors
  ZD: '#3d5a3d', // zombie dark green
  ZL: '#5d8a5d', // zombie light green
  ZS: '#4d6a4d', // zombie skin
  // Skeleton colors
  SD: '#a0a0a0', // skeleton dark bone
  SL: '#d4d4d4', // skeleton light bone
  SB: '#c0c0c0', // skeleton bone
  // Spider colors
  PD: '#2a2a2a', // spider dark
  PL: '#4a4a4a', // spider light
  PR: '#8a2222', // spider red eyes
  // Wolf colors
  WD: '#4a4a5a', // wolf dark
  WL: '#7a7a8a', // wolf light
  WN: '#3a3a4a', // wolf nose
  // Orc colors
  OD: '#2a4a2a', // orc dark green
  OL: '#4a7a4a', // orc light green
  OT: '#5a3a2a', // orc teeth/tusk
  // Golem colors
  GD: '#5a4a3a', // golem dark stone
  GL: '#8a7a6a', // golem light stone
  GC: '#6a5a4a', // golem crack
  GE: '#44ff44', // golem eyes (green glow)
  // Demon colors
  DD: '#6a1111', // demon dark red
  DL: '#aa3333', // demon light red
  DF: '#ff4400', // demon fire
  DH: '#331111', // demon horn
  // Ghost colors
  HD: '#7799cc', // ghost dark
  HL: '#aaccff', // ghost light
  HE: '#ffffff', // ghost eyes
  // Common
  BK: '#000000', // black
  RD: '#ff0000', // red (eyes)
  WT: '#ffffff', // white
};

type Color = string | null;

// 12x12 pixel sprites for enemies (larger than items for better detail)
const SPRITE_SIZE = 12;

// Zombie sprite - shambling undead
const zombieSprite: Color[][] = [
  [C._,C._,C._,C.ZD,C.ZD,C.ZD,C.ZD,C.ZD,C._,C._,C._,C._],
  [C._,C._,C.ZD,C.ZS,C.ZS,C.ZS,C.ZS,C.ZS,C.ZD,C._,C._,C._],
  [C._,C._,C.ZD,C.ZS,C.RD,C.ZS,C.ZS,C.RD,C.ZD,C._,C._,C._],
  [C._,C._,C.ZD,C.ZS,C.ZS,C.ZS,C.ZS,C.ZS,C.ZD,C._,C._,C._],
  [C._,C._,C._,C.ZD,C.ZS,C.BK,C.ZS,C.ZD,C._,C._,C._,C._],
  [C._,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C._,C._,C._],
  [C.ZD,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C.ZL,C.ZD,C._,C._],
  [C._,C.ZD,C._,C._,C.ZL,C.ZL,C.ZL,C._,C._,C.ZD,C._,C._],
  [C._,C.ZD,C._,C._,C.ZL,C.ZL,C.ZL,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.ZD,C._,C.ZD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.ZD,C._,C.ZD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.ZD,C.ZD,C._,C.ZD,C.ZD,C._,C._,C._,C._],
];

// Skeleton sprite - bony warrior
const skeletonSprite: Color[][] = [
  [C._,C._,C._,C._,C.SD,C.SD,C.SD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.SD,C.SL,C.SL,C.SL,C.SD,C._,C._,C._,C._],
  [C._,C._,C._,C.SD,C.BK,C.SL,C.BK,C.SD,C._,C._,C._,C._],
  [C._,C._,C._,C.SD,C.SL,C.SL,C.SL,C.SD,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.SD,C.BK,C.SD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.SB,C._,C._,C._,C._,C._,C._],
  [C._,C._,C.SD,C.SB,C.SB,C.SB,C.SB,C.SB,C.SD,C._,C._,C._],
  [C._,C._,C._,C._,C._,C.SB,C._,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.SB,C.SB,C.SB,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.SD,C._,C.SD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.SB,C._,C.SB,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.SD,C.SD,C._,C.SD,C.SD,C._,C._,C._,C._],
];

// Spider sprite - 8-legged menace
const spiderSprite: Color[][] = [
  [C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._],
  [C.PD,C._,C._,C._,C._,C._,C._,C._,C._,C._,C.PD,C._],
  [C._,C.PD,C._,C._,C.PL,C.PL,C.PL,C._,C._,C.PD,C._,C._],
  [C._,C._,C.PD,C.PL,C.PR,C.PL,C.PR,C.PL,C.PD,C._,C._,C._],
  [C.PD,C._,C.PL,C.PL,C.PL,C.PL,C.PL,C.PL,C.PL,C._,C.PD,C._],
  [C._,C.PD,C.PL,C.PL,C.PL,C.PD,C.PL,C.PL,C.PL,C.PD,C._,C._],
  [C.PD,C.PL,C.PL,C.PL,C.PL,C.PL,C.PL,C.PL,C.PL,C.PL,C.PD,C._],
  [C._,C.PD,C._,C.PL,C.PL,C.PL,C.PL,C.PL,C._,C.PD,C._,C._],
  [C.PD,C._,C._,C._,C.PD,C.PD,C.PD,C._,C._,C._,C.PD,C._],
  [C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._],
];

// Wolf sprite - fierce predator
const wolfSprite: Color[][] = [
  [C._,C._,C.WD,C._,C._,C._,C._,C._,C.WD,C._,C._,C._],
  [C._,C._,C.WL,C.WD,C._,C._,C._,C.WD,C.WL,C._,C._,C._],
  [C._,C._,C.WL,C.WL,C.WD,C.WD,C.WD,C.WL,C.WL,C._,C._,C._],
  [C._,C._,C._,C.WL,C.RD,C.WL,C.RD,C.WL,C._,C._,C._,C._],
  [C._,C._,C._,C.WL,C.WL,C.WN,C.WL,C.WL,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.WL,C.WL,C.WL,C._,C._,C._,C._,C._],
  [C._,C.WD,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WD,C._,C._],
  [C.WD,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WD,C._],
  [C._,C.WD,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WL,C.WD,C.WL,C.WD],
  [C._,C._,C.WD,C._,C._,C._,C._,C._,C.WD,C._,C.WL,C.WD],
  [C._,C._,C.WD,C._,C._,C._,C._,C._,C.WD,C._,C._,C.WD],
  [C._,C.WD,C.WD,C._,C._,C._,C._,C.WD,C.WD,C._,C._,C._],
];

// Orc sprite - brutish warrior
const orcSprite: Color[][] = [
  [C._,C._,C._,C.OD,C.OD,C.OD,C.OD,C.OD,C.OD,C._,C._,C._],
  [C._,C._,C.OD,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OD,C._,C._],
  [C._,C._,C.OD,C.OL,C.RD,C.OL,C.OL,C.RD,C.OL,C.OD,C._,C._],
  [C._,C._,C.OD,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OD,C._,C._],
  [C._,C.OT,C.OD,C.OL,C.OL,C.BK,C.OL,C.OL,C.OD,C.OT,C._,C._],
  [C._,C._,C._,C.OD,C.OD,C.OD,C.OD,C.OD,C._,C._,C._,C._],
  [C._,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C._,C._],
  [C.OD,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OL,C.OD,C._],
  [C._,C.OD,C._,C.OL,C.OL,C.OL,C.OL,C.OL,C._,C.OD,C._,C._],
  [C._,C._,C._,C._,C.OD,C._,C.OD,C._,C._,C._,C._,C._],
  [C._,C._,C._,C._,C.OL,C._,C.OL,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.OD,C.OD,C._,C.OD,C.OD,C._,C._,C._,C._],
];

// Golem sprite - stone giant
const golemSprite: Color[][] = [
  [C._,C._,C.GD,C.GD,C.GD,C.GD,C.GD,C.GD,C.GD,C.GD,C._,C._],
  [C._,C.GD,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GD,C._],
  [C._,C.GD,C.GL,C.GE,C.GL,C.GC,C.GL,C.GE,C.GL,C.GL,C.GD,C._],
  [C._,C.GD,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GD,C._],
  [C._,C.GD,C.GL,C.GL,C.GC,C.GC,C.GC,C.GL,C.GL,C.GL,C.GD,C._],
  [C._,C._,C.GD,C.GD,C.GD,C.GD,C.GD,C.GD,C.GD,C.GD,C._,C._],
  [C.GD,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GD],
  [C.GD,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GD],
  [C._,C.GD,C.GD,C.GL,C.GL,C.GL,C.GL,C.GL,C.GL,C.GD,C.GD,C._],
  [C._,C._,C._,C.GD,C.GL,C._,C.GL,C.GD,C._,C._,C._,C._],
  [C._,C._,C._,C.GD,C.GL,C._,C.GL,C.GD,C._,C._,C._,C._],
  [C._,C._,C.GD,C.GD,C.GD,C._,C.GD,C.GD,C.GD,C._,C._,C._],
];

// Demon sprite - fiery beast
const demonSprite: Color[][] = [
  [C.DH,C._,C._,C._,C._,C._,C._,C._,C._,C._,C.DH,C._],
  [C._,C.DH,C._,C.DD,C.DD,C.DD,C.DD,C.DD,C._,C.DH,C._,C._],
  [C._,C._,C.DD,C.DL,C.DL,C.DL,C.DL,C.DL,C.DD,C._,C._,C._],
  [C._,C._,C.DD,C.DL,C.DF,C.DL,C.DF,C.DL,C.DD,C._,C._,C._],
  [C._,C._,C.DD,C.DL,C.DL,C.DL,C.DL,C.DL,C.DD,C._,C._,C._],
  [C._,C._,C._,C.DD,C.DL,C.BK,C.DL,C.DD,C._,C._,C._,C._],
  [C._,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C._,C._],
  [C.DD,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DD,C._],
  [C._,C.DD,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DL,C.DD,C._,C._],
  [C._,C._,C._,C.DD,C.DD,C._,C.DD,C.DD,C._,C._,C._,C._],
  [C._,C._,C._,C.DD,C.DL,C._,C.DL,C.DD,C._,C._,C._,C._],
  [C._,C._,C.DD,C.DF,C.DD,C._,C.DD,C.DF,C.DD,C._,C._,C._],
];

// Ghost sprite - ethereal specter
const ghostSprite: Color[][] = [
  [C._,C._,C._,C._,C.HL,C.HL,C.HL,C._,C._,C._,C._,C._],
  [C._,C._,C._,C.HL,C.HL,C.HL,C.HL,C.HL,C._,C._,C._,C._],
  [C._,C._,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C._,C._,C._],
  [C._,C._,C.HL,C.HL,C.BK,C.HL,C.BK,C.HL,C.HL,C._,C._,C._],
  [C._,C._,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C._,C._,C._],
  [C._,C._,C.HD,C.HL,C.HL,C.BK,C.HL,C.HL,C.HD,C._,C._,C._],
  [C._,C.HD,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HD,C._,C._],
  [C._,C.HD,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HD,C._,C._],
  [C._,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C.HL,C._,C._],
  [C.HL,C.HL,C._,C.HL,C.HL,C._,C.HL,C.HL,C._,C.HL,C.HL,C._],
  [C.HD,C._,C._,C.HD,C._,C._,C._,C.HD,C._,C._,C.HD,C._],
  [C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._,C._],
];

// Map enemy types to their sprites
const spriteMap: { [key: string]: Color[][] } = {
  zombie: zombieSprite,
  skeleton: skeletonSprite,
  spider: spiderSprite,
  wolf: wolfSprite,
  orc: orcSprite,
  golem: golemSprite,
  demon: demonSprite,
  ghost: ghostSprite,
};

/**
 * Render an enemy sprite at the given screen position
 * @param ctx Canvas 2D context
 * @param enemyType The type of enemy
 * @param screenX Screen X position (center)
 * @param screenY Screen Y position (bottom)
 * @param size The size to render (width and height)
 * @param flash Whether to flash red (for attack animation)
 * @returns true if sprite was rendered, false if not found
 */
export function renderEnemySprite(
  ctx: CanvasRenderingContext2D,
  enemyType: string,
  screenX: number,
  screenY: number,
  size: number,
  flash: boolean = false
): boolean {
  const sprite = spriteMap[enemyType];
  if (!sprite) return false;

  const pixelSize = size / SPRITE_SIZE;
  const startX = screenX - size / 2;
  const startY = screenY - size;

  for (let row = 0; row < SPRITE_SIZE; row++) {
    for (let col = 0; col < SPRITE_SIZE; col++) {
      const color = sprite[row]?.[col];
      if (color) {
        // Apply red tint if flashing
        if (flash) {
          ctx.fillStyle = blendWithRed(color);
        } else {
          ctx.fillStyle = color;
        }
        ctx.fillRect(
          startX + col * pixelSize,
          startY + row * pixelSize,
          pixelSize + 0.5, // Small overlap to prevent gaps
          pixelSize + 0.5
        );
      }
    }
  }

  return true;
}

/**
 * Blend a color with red for attack flash effect
 */
function blendWithRed(color: string): string {
  // Parse hex color
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  // Blend towards red
  const newR = Math.min(255, r + 100);
  const newG = Math.max(0, g - 30);
  const newB = Math.max(0, b - 30);

  return `rgb(${newR}, ${newG}, ${newB})`;
}

/**
 * Check if a sprite exists for the given enemy type
 */
export function hasEnemySprite(enemyType: string): boolean {
  return enemyType in spriteMap;
}
